/**
 * useNotifications — current user's in-app notifications.
 *
 * Rows are created exclusively by DB triggers (task assigned, comment
 * added, doubt raised/answered, task done). This hook:
 *   - loads the latest 30 on mount
 *   - prepends new ones live via a realtime INSERT subscription
 *   - exposes markRead / markAllRead with optimistic local updates
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../api/supabase.js';
import { useAuth } from '../context/AuthContext.jsx';

const PAGE_SIZE = 30;

let notifChannelSeq = 0;

export function useNotifications() {
  const { profile } = useAuth();
  const userId = profile?.id ?? null;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetchItems = useCallback(async () => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE);

    if (!mountedRef.current) return;
    if (error) {
      console.error('[ORIN] Failed to fetch notifications:', error.message);
    } else {
      setItems(data || []);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchItems();

    if (!userId) return undefined;

    const channel = supabase
      .channel(`notifications-${userId}-${++notifChannelSeq}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (!mountedRef.current || !payload.new?.id) return;
          setItems((current) => {
            if (current.some((n) => n.id === payload.new.id)) return current;
            return [payload.new, ...current].slice(0, PAGE_SIZE * 2);
          });
        },
      )
      .subscribe();

    return () => {
      mountedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [userId, fetchItems]);

  const markRead = useCallback(async (id) => {
    if (!id) return;
    setItems((current) => current.map((n) => (n.id === id ? { ...n, read: true } : n)));
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);
    if (error) console.error('[ORIN] Failed to mark notification read:', error.message);
  }, []);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    setItems((current) => current.map((n) => (n.read ? n : { ...n, read: true })));
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);
    if (error) console.error('[ORIN] Failed to mark all read:', error.message);
  }, [userId]);

  const unreadCount = items.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);

  return { items, unreadCount, loading, markRead, markAllRead, refresh: fetchItems };
}
